import { useEffect, useState } from 'react'
import { fetchCameras, fetchSiteMap, type CameraTamper, type SiteMap } from '../api'
import type { CameraStatus, Cluster } from '../types'
import { isInProgress } from '../activity'
import { useFinishTick } from '../useFinishTick'
import { cameraLabel, describe, type CameraState } from '../siteMap'
import { SitePlan } from './SitePlan'
import { ZoomControls } from './ZoomControls'

const label = 'text-[13px] text-zinc-400'

// cameras is null when the site could not be asked for its camera list.
type Loaded = { siteId: string; map: SiteMap; cameras: string[] | null }

export interface SiteMapPaneProps {
  siteId: string
  siteName: string
  watching: string
  health: Record<string, CameraStatus>
  tamper: Record<string, CameraTamper>
  clusters: Cluster[]
  onSelectCamera: (camera: string) => void
}

/**
 * The Action Center while a dispatcher watches a camera with no incident open:
 * the site's plan with its cameras on it, each a way to switch live view.
 */
export function SiteMapPane({ siteId, siteName, watching, health, tamper, clusters, onSelectCamera }: SiteMapPaneProps) {
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [unreadable, setUnreadable] = useState<string | null>(null)
  // Back to fitting the plan whenever the site changes.
  const [zoomed, setZoomed] = useState<{ siteId: string; zoom: number }>({ siteId, zoom: 1 })
  const zoom = zoomed.siteId === siteId ? zoomed.zoom : 1
  const setZoom = (z: number) => setZoomed({ siteId, zoom: z })

  useEffect(() => {
    let cancelled = false
    Promise.all([fetchSiteMap(siteId), fetchCameras(siteId).catch(() => null)])
      .then(([map, cameras]) => { if (!cancelled) setLoaded({ siteId, map, cameras }) })
      .catch(() => { if (!cancelled) setUnreadable(siteId) })
    return () => { cancelled = true }
  }, [siteId])

  const siteClusters = clusters.filter((c) => c.site_id === siteId)
  useFinishTick(siteClusters)
  const stateOf = (camera: string): CameraState => ({
    health: health[camera] ?? 'unknown',
    tamper: tamper[camera],
    inProgress: siteClusters.some((c) => c.camera === camera && isInProgress(c)),
  })

  const current = loaded?.siteId === siteId ? loaded : null
  const onPlan = new Set(current?.map.placements.map((p) => p.camera))
  // With a plan, every other camera is listed too, placed or not, so switching
  // never needs its marker found on the plan (#280).
  const others = current?.map.has_plan && current.cameras ? current.cameras.filter((c) => c !== watching) : []

  return (
    <aside
      aria-label="Site map"
      className="w-[23.75rem] shrink-0 flex flex-col gap-3.5 bg-zinc-900 border-l border-zinc-800 px-[1.125rem] pt-[1.125rem] pb-4 relative overflow-y-auto scrollbar-thin"
    >
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold text-zinc-100">Site map</span>
        <span className="flex-1 text-sm text-zinc-400 truncate">{siteName}</span>
      </div>

      {unreadable === siteId && !current && (
        <p role="alert" className="text-xs text-fault">Could not load the map of {siteName}.</p>
      )}

      {current?.map.has_plan && (
        <div className="relative shrink-0">
          <SitePlan
            siteId={siteId}
            siteName={siteName}
            map={current.map}
            stateOf={stateOf}
            current={watching}
            currentIs="watching"
            onSelect={onSelectCamera}
            hint={(camera) => (camera === watching ? 'Watching now' : 'Click to open its live view')}
            // Whatever the pane has left under the top bar, its title, the
            // watching card and the key (296px measured), so they stay in view.
            maxHeight="max(16rem, calc(100dvh - 19rem))"
            zoom={zoom}
          />
          <ZoomControls zoom={zoom} onZoom={setZoom} />
        </div>
      )}

      {current?.map.has_plan && (
        <>
          <WatchingCard
            camera={watching}
            place={current.map.placements.find((p) => p.camera === watching)?.place}
            state={stateOf(watching)}
          />
          <Key />
        </>
      )}

      {current && !current.map.has_plan && (
        <>
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-8 rounded-md border border-dashed border-zinc-700 text-center">
            <svg className="w-8 h-8 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
              <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />
            </svg>
            <span className="text-sm font-semibold text-zinc-200">No site map yet</span>
            <span className="text-xs leading-relaxed text-zinc-400">
              An administrator can upload a plan of {siteName} and place its cameras in the Overwatch Console.
            </span>
          </div>
          {current.cameras && (
            <div className="flex flex-col gap-1.5">
              <span id="site-map-cameras" className={label}>Cameras</span>
              <ul aria-labelledby="site-map-cameras" className="flex flex-col gap-1.5">
                {current.cameras.map((c) => (
                  <li key={c}>
                    <CameraRow camera={c} state={stateOf(c)} watching={c === watching} onSelect={() => onSelectCamera(c)} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {current && current.cameras === null && (
        <p className="text-xs text-attention">Could not reach {siteName} for its camera list.</p>
      )}

      {others.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span id="site-map-others" className="text-[13px] text-zinc-400">Other cameras</span>
          <ul aria-labelledby="site-map-others" className="flex flex-col gap-1.5">
            {others.map((c) => (
              <li key={c}>
                <CameraRow camera={c} state={stateOf(c)} watching={false} unplaced={!onPlan.has(c)} onSelect={() => onSelectCamera(c)} />
              </li>
            ))}
          </ul>
          {others.some((c) => !onPlan.has(c)) && (
            <p className="text-xs leading-relaxed text-zinc-400">An administrator places cameras in the Overwatch Console.</p>
          )}
        </div>
      )}
    </aside>
  )
}

function WatchingCard({ camera, place, state }: { camera: string; place?: string; state: CameraState }) {
  const detail = [place, describe(state).join(', ')].filter(Boolean).join(' · ')
  return (
    <div role="status" className="flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-zinc-950 border border-zinc-800">
      <span aria-hidden className="w-2 h-2 shrink-0 rounded-full bg-orange-400" />
      <div className="flex flex-col gap-px min-w-0">
        <span aria-hidden className="text-[10px] font-bold uppercase tracking-[0.15em] text-orange-300">Watching</span>
        <span className="text-[13px] font-semibold text-zinc-100 break-all"><span className="sr-only">Watching </span>{camera}</span>
        {detail && <span className="text-[11px] text-zinc-400">{detail}</span>}
      </div>
    </div>
  )
}

// The swatches copy SitePlan's Marker colours by hand; a marker restyled
// without its swatch leaves the key describing something no longer on the plan.
function Key() {
  const item = 'flex items-center gap-1.5'
  return (
    <ul aria-label="Key" className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] text-zinc-400">
      <li className={item}><span aria-hidden className="w-2.5 h-2.5 rounded-full bg-orange-400 border border-orange-300 shadow-[0_0_0_2px_rgb(249_115_22/0.25)]" />Watching</li>
      <li className={item}><span aria-hidden className="w-2 h-2 rounded-full bg-live" />Incident in progress</li>
      <li className={item}><span aria-hidden className="w-2.5 h-2.5 rounded-full border-2 border-fault" />Offline</li>
      <li className={item}><span aria-hidden className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-degraded" />Degraded</li>
      <li className={item}><span aria-hidden className="w-3 h-3 rounded-full bg-attention text-zinc-950 text-[9px] font-extrabold leading-3 text-center before:content-['!']" />View lost / covered</li>
      <li className={item}>
        <svg aria-hidden className="w-3 h-3" viewBox="0 0 12 12"><path d="M6 11L1 2h10z" fill="rgb(161 161 170 / 0.35)" /></svg>
        Where it faces
      </li>
    </ul>
  )
}

// The state sits under the name rather than beside it: site camera names run
// long and differ only at the end, so a name cut short to fit the state on the
// same line hides the part that tells cameras apart.
function CameraRow({ camera, state, watching, unplaced = false, onSelect }: { camera: string; state: CameraState; watching: boolean; unplaced?: boolean; onSelect: () => void }) {
  const words = watching ? ['watching'] : [...describe(state), ...(unplaced ? ['Not on the map'] : [])]
  return (
    <button
      type="button"
      aria-label={cameraLabel(camera, undefined, state, watching)}
      aria-current={watching ? 'true' : undefined}
      onClick={onSelect}
      className={`w-full min-h-10 flex flex-col justify-center gap-0.5 px-3 py-2 rounded-md border text-left text-sm ${watching ? 'border-orange-300 bg-orange-500/10 text-zinc-100' : 'border-zinc-800 text-zinc-100 hover:bg-zinc-800'}`}
    >
      <span className="break-all">{camera}</span>
      {words.length > 0 && (
        <span className={`flex items-center gap-1.5 text-xs ${state.health === 'offline' ? 'text-fault' : state.tamper ? 'text-attention' : 'text-zinc-400'}`}>
          {state.inProgress && <span aria-hidden className="w-[7px] h-[7px] rounded-full bg-live" />}
          {words.join(', ')}
        </span>
      )}
    </button>
  )
}
